import React, { useCallback, useEffect, useState } from 'react'
import { api } from '../lib/api'
import InlineError from '../ui/InlineError'
import MenuGuideButton from '../components/MenuGuideButton'
import Loading from '../ui/Loading'

function unwrapData(res) {
  const p = res?.data
  if (p?.data !== undefined) return p.data
  return p ?? null
}

const emptyCreateForm = () => ({
  slug: '',
  title: '',
  summary: '',
  viewUrl: '',
  downloadUrl: '',
  sortOrder: '',
  published: true,
})

export default function OpsSupportGuideManualsPage() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [saveMsg, setSaveMsg] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [createForm, setCreateForm] = useState(emptyCreateForm)
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    setErr('')
    setSaveMsg('')
    setLoading(true)
    try {
      const res = await api.get('/ops/support-guide/manuals')
      const data = unwrapData(res)
      setRows(Array.isArray(data) ? data : [])
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || '불러오기에 실패했습니다.')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const updateRow = (id, field, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)))
  }

  const handleCreate = async () => {
    setErr('')
    setSaveMsg('')
    setCreating(true)
    try {
      const sortOrder =
        createForm.sortOrder === '' || createForm.sortOrder == null
          ? null
          : Number(createForm.sortOrder)
      const body = {
        slug: createForm.slug.trim(),
        title: createForm.title.trim(),
        summary: createForm.summary || '',
        viewUrl: createForm.viewUrl || '',
        downloadUrl: createForm.downloadUrl || '',
        sortOrder: Number.isFinite(sortOrder) ? sortOrder : null,
        published: Boolean(createForm.published),
      }
      const res = await api.post('/ops/support-guide/manuals', body)
      const data = unwrapData(res)
      setRows(Array.isArray(data) ? data : [])
      setCreateForm(emptyCreateForm())
      setShowAdd(false)
      setSaveMsg('새 매뉴얼을 등록했습니다.')
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || '등록에 실패했습니다.')
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('이 매뉴얼을 삭제할까요? 공개 가이드에서도 사라집니다.')) return
    setErr('')
    setSaveMsg('')
    setSaving(true)
    try {
      const res = await api.delete(`/ops/support-guide/manuals/${id}`)
      const data = unwrapData(res)
      setRows(Array.isArray(data) ? data : [])
      setSaveMsg('삭제했습니다.')
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || '삭제에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async () => {
    setErr('')
    setSaveMsg('')
    setSaving(true)
    try {
      const payload = rows.map((r) => ({
        id: r.id,
        title: r.title || '',
        summary: r.summary ?? '',
        viewUrl: r.viewUrl ?? '',
        downloadUrl: r.downloadUrl ?? '',
        sortOrder: Number(r.sortOrder) || 0,
        published: Boolean(r.published),
      }))
      const res = await api.put('/ops/support-guide/manuals', { items: payload })
      const data = unwrapData(res)
      setRows(Array.isArray(data) ? data : [])
      setSaveMsg('저장했습니다.')
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || '저장에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <Loading />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">운영관리 · 기타관리</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900">이용 가이드 · 매뉴얼 관리</h1>
            <MenuGuideButton slug="ops-management" title="운영관리 가이드" anchor="step-03" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setShowAdd((v) => !v)
              setErr('')
              setSaveMsg('')
            }}
            className="rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100"
            disabled={saving || creating}
          >
            {showAdd ? '추가 폼 닫기' : '새 매뉴얼 추가'}
          </button>
          <button
            type="button"
            onClick={load}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
            disabled={saving}
          >
            새로고침
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-bold text-white hover:bg-brand-navy/90 disabled:opacity-60"
            disabled={saving}
          >
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>

      {err ? <InlineError message={err} /> : null}
      {saveMsg ? <p className="text-sm font-medium text-emerald-700">{saveMsg}</p> : null}

      {showAdd ? (
        <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-5 shadow-sm">
          <h2 className="text-sm font-bold text-slate-900">새 매뉴얼 등록</h2>
          <p className="mt-1 text-xs text-slate-600">
            slug 예: <code className="rounded bg-white px-1">billing-guide</code> · 순서를 비우면 목록 맨 아래(+10)로 붙습니다.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label className="block text-xs font-semibold text-slate-700">
              slug *
              <input
                className="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 font-mono text-sm"
                value={createForm.slug}
                onChange={(e) => setCreateForm((f) => ({ ...f, slug: e.target.value }))}
                placeholder="영문-소문자-숫자-하이픈"
              />
            </label>
            <label className="block text-xs font-semibold text-slate-700 sm:col-span-2">
              제목 *
              <input
                className="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 text-sm"
                value={createForm.title}
                onChange={(e) => setCreateForm((f) => ({ ...f, title: e.target.value }))}
              />
            </label>
            <label className="block text-xs font-semibold text-slate-700 sm:col-span-2 lg:col-span-3">
              요약
              <textarea
                className="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 text-sm"
                rows={2}
                value={createForm.summary}
                onChange={(e) => setCreateForm((f) => ({ ...f, summary: e.target.value }))}
              />
            </label>
            <label className="block text-xs font-semibold text-slate-700 sm:col-span-2">
              보기 URL
              <input
                className="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 font-mono text-xs"
                value={createForm.viewUrl}
                onChange={(e) => setCreateForm((f) => ({ ...f, viewUrl: e.target.value }))}
                placeholder="비우면 내장 HTML (또는 https://…)"
              />
            </label>
            <label className="block text-xs font-semibold text-slate-700 sm:col-span-2">
              다운로드 URL
              <input
                className="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 font-mono text-xs"
                value={createForm.downloadUrl}
                onChange={(e) => setCreateForm((f) => ({ ...f, downloadUrl: e.target.value }))}
                placeholder="비우면 내장 PDF (또는 https://…)"
              />
            </label>
            <label className="block text-xs font-semibold text-slate-700">
              순서(비우면 자동)
              <input
                type="number"
                className="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 text-sm tabular-nums"
                value={createForm.sortOrder}
                onChange={(e) => setCreateForm((f) => ({ ...f, sortOrder: e.target.value }))}
              />
            </label>
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 sm:col-span-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={Boolean(createForm.published)}
                onChange={(e) => setCreateForm((f) => ({ ...f, published: e.target.checked }))}
              />
              공개(이용 가이드에 노출)
            </label>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating || !createForm.slug.trim() || !createForm.title.trim()}
              className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-bold text-white hover:bg-brand-navy/90 disabled:opacity-50"
            >
              {creating ? '등록 중…' : '등록하기'}
            </button>
            <button
              type="button"
              onClick={() => {
                setCreateForm(emptyCreateForm())
                setShowAdd(false)
              }}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
            >
              취소
            </button>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-[960px] w-full border-collapse text-left text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
            <tr>
              <th className="border-b border-slate-200 px-3 py-3">slug</th>
              <th className="border-b border-slate-200 px-3 py-3">제목</th>
              <th className="border-b border-slate-200 px-3 py-3">요약</th>
              <th className="border-b border-slate-200 px-3 py-3">보기 URL</th>
              <th className="border-b border-slate-200 px-3 py-3">다운로드 URL</th>
              <th className="border-b border-slate-200 px-3 py-3">순서</th>
              <th className="border-b border-slate-200 px-3 py-3">공개</th>
              <th className="border-b border-slate-200 px-3 py-3">삭제</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="align-top">
                <td className="border-b border-slate-100 px-3 py-2 font-mono text-xs text-slate-500">{r.slug}</td>
                <td className="border-b border-slate-100 px-3 py-2">
                  <input
                    className="w-full min-w-[10rem] rounded border border-slate-200 px-2 py-1 text-sm"
                    value={r.title || ''}
                    onChange={(e) => updateRow(r.id, 'title', e.target.value)}
                  />
                </td>
                <td className="border-b border-slate-100 px-3 py-2">
                  <textarea
                    className="w-full min-w-[14rem] rounded border border-slate-200 px-2 py-1 text-sm"
                    rows={3}
                    value={r.summary || ''}
                    onChange={(e) => updateRow(r.id, 'summary', e.target.value)}
                  />
                </td>
                <td className="border-b border-slate-100 px-3 py-2">
                  <input
                    className="w-full min-w-[12rem] rounded border border-slate-200 px-2 py-1 font-mono text-xs"
                    value={r.viewUrl || ''}
                    onChange={(e) => updateRow(r.id, 'viewUrl', e.target.value)}
                    placeholder="https://…"
                  />
                </td>
                <td className="border-b border-slate-100 px-3 py-2">
                  <input
                    className="w-full min-w-[12rem] rounded border border-slate-200 px-2 py-1 font-mono text-xs"
                    value={r.downloadUrl || ''}
                    onChange={(e) => updateRow(r.id, 'downloadUrl', e.target.value)}
                    placeholder="https://…"
                  />
                </td>
                <td className="border-b border-slate-100 px-3 py-2">
                  <input
                    type="number"
                    className="w-20 rounded border border-slate-200 px-2 py-1 text-sm tabular-nums"
                    value={r.sortOrder ?? 0}
                    onChange={(e) => updateRow(r.id, 'sortOrder', Number(e.target.value))}
                  />
                </td>
                <td className="border-b border-slate-100 px-3 py-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300"
                    checked={Boolean(r.published)}
                    onChange={(e) => updateRow(r.id, 'published', e.target.checked)}
                  />
                </td>
                <td className="border-b border-slate-100 px-3 py-2">
                  <button
                    type="button"
                    onClick={() => handleDelete(r.id)}
                    className="text-xs font-semibold text-rose-600 hover:underline disabled:opacity-50"
                    disabled={saving || creating}
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
